import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import * as React from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DAY_LONG_MONTH, INTL_DAY_LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import OptionsButton from 'tripPlanner/components/Documents/OptionsButton'
import { OptionsMenuActions, OptionsMenuButton } from 'tripPlanner/components/Documents/OptionsMenuActions'
import RecentItemInlineTitleEdit from 'tripPlanner/pages/chat/RecentItemInlineTitleEdit'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  min-height: ${rem(66)};
  padding: ${rem(14)} ${rem(12)};
  background: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};
  text-align: left;
  outline: none;
  cursor: pointer;
  box-shadow: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  ${mediaHoverable} {
    &:hover {
      box-shadow: ${props => props.theme.shadow.bottom.small};
    }
  }

  &:focus-visible {
    border-color: ${props => props.theme.palette.brand.primary.normal};
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(4)};
  width: 100%;
`

const TitleContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${rem(2)};
  min-width: 0;
`

const Timestamp = styled(BodyText)`
  margin-top: ${rem(8)};
`

interface Props {
  title?: string
  subtitle?: string
  updatedAt: string
  onClick: () => void
  onRename: (name: string) => Promise<void>
  onDeleteClick: (closeMenu: () => void) => (event: React.MouseEvent) => void
}

export function getRecentChatTimestampLabel(
  updatedAt: string,
  formatMessage: IntlShape['formatMessage'],
  now = moment(),
): string {
  const updatedAtMoment = moment(updatedAt)
  if (!updatedAtMoment.isValid()) {
    return intlDateFormatter(updatedAt, INTL_DAY_LONG_MONTH)
  }

  const dateFormat = updatedAtMoment.year() === now.year() ? INTL_DAY_LONG_MONTH : INTL_DAY_LONG_MONTH_LONG_YEAR
  if (updatedAtMoment.isAfter(now)) {
    return intlDateFormatter(updatedAt, dateFormat)
  }

  const elapsed = moment.duration(now.diff(updatedAtMoment))
  const elapsedMinutes = elapsed.asMinutes()
  const elapsedHours = elapsed.asHours()
  const daysAgo = now.diff(updatedAtMoment, 'days')

  if (elapsedMinutes < 60) {
    const minutesAgo = Math.max(1, Math.floor(elapsedMinutes))

    return formatMessage({
      id: 'WsgRYtkbjuV_vQsqimvA',
      defaultMessage: '{count, plural, one {# minute ago} other {# minutes ago}}',
      description: 'Recent chat timestamp shown when the chat was updated less than one day ago',
    }, { count: minutesAgo })
  }

  if (elapsedHours < 24) {
    const hoursAgo = Math.max(1, Math.min(23, Math.round(elapsedHours)))

    return formatMessage({
      id: 'eoO6OW120iob3ywV6CWK',
      defaultMessage: '{count, plural, one {# hour ago} other {# hours ago}}',
      description: 'Recent chat timestamp shown when the chat was updated less than one day ago but at least one hour ago',
    }, { count: hoursAgo })
  }

  if (daysAgo <= 7) {
    return formatMessage({
      id: 'PHT7fqRxB3FBB7NfmiST',
      defaultMessage: '{count, plural, one {# day ago} other {# days ago}}',
      description: 'Recent chat timestamp shown when the chat was updated within the last seven days',
    }, { count: daysAgo })
  }

  return intlDateFormatter(updatedAt, dateFormat)
}

export default function ChatLandingRecentChatTile({ title, subtitle, updatedAt, onClick, onRename, onDeleteClick }: Props) {
  const intl = useIntl()
  const [isRenaming, setIsRenaming] = React.useState(false)
  const formattedDate = getRecentChatTimestampLabel(updatedAt, intl.formatMessage)
  const untitledLabel = intl.formatMessage({
    id: 'P7xyHLCCjSlOklqXsVYg',
    defaultMessage: 'Untitled',
    description: 'Default title for untitled trip in the trip planner feature',
  })
  const actionsTitle = intl.formatMessage({
    id: 'pi4uwekopu4hL5smnBsK',
    defaultMessage: 'Recent chat actions',
    description: 'Accessible title for the recent chat actions dropdown menu on the trip planner landing page',
  })
  const deleteText = intl.formatMessage({
    id: 'cWmw1tD3F1HR1j-s27sR',
    defaultMessage: 'Delete',
    description: 'Button label to delete a recent chat or trip from the trip planner landing page',
  })
  const renameText = intl.formatMessage({
    id: '04s1xvbNcf0Iw3ACds_n',
    defaultMessage: 'Rename',
    description: 'Button label to rename a recent chat or trip from the trip planner landing page',
  })

  const handleRenameClick = (closeMenu: () => void) => (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    closeMenu()
    setIsRenaming(true)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.currentTarget !== event.target) {
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick()
    }
  }

  return (
    <Container
      role={isRenaming ? undefined : 'button'}
      tabIndex={isRenaming ? undefined : 0}
      onClick={isRenaming ? undefined : onClick}
      onKeyDown={isRenaming ? undefined : handleKeyDown}
    >
      <Header>
        <TitleContent>
          <RecentItemInlineTitleEdit
            title={title}
            fallbackTitle={untitledLabel}
            isEditing={isRenaming}
            variant="small"
            onEditingChange={setIsRenaming}
            onRename={onRename}
          />
          {!!subtitle && (
            <BodyText variant="small" weight="normal" align="start" colour="light-foreground-alt" lineClamp={1}>
              {subtitle}
            </BodyText>
          )}
        </TitleContent>
        <OptionsButton
          title={actionsTitle}
          bodyContent={(closeMenu) => (
            <OptionsMenuActions>
              <OptionsMenuButton type="button" onClick={handleRenameClick(closeMenu)}>
                {renameText}
              </OptionsMenuButton>
              <OptionsMenuButton type="button" onClick={onDeleteClick(closeMenu)}>
                {deleteText}
              </OptionsMenuButton>
            </OptionsMenuActions>
          )}
        />
      </Header>
      <Timestamp variant="small" weight="normal" align="start" colour="normal-contrast" lineClamp={1}>
        {formattedDate}
      </Timestamp>
    </Container>
  )
}
